import { createSlice } from '@reduxjs/toolkit';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import { AnalyticCountModel } from '../models/AnalyticCount.model';
import { LogDateCountModel } from '../models/LogDateCount.model';
import { TopContentModel } from '../models/TopContent.model';
import { ExtendedAnalyticTypes, InsightsFilter } from '../types/AnalyticsTypes';
import {
  getAnalyticCount,
  getLogDateCount,
  getSystemInsights,
  getTopContent,
  getUserInsights,
} from './operators';
import { SystemInsightsModel } from '../models/SystemInsights.model';
import { UserInsightsModel } from '../models/UserInsights.model';

export interface AnalyticsState {
  logCount: LogDateCountModel[];
  getLogDateCountStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  topContent: TopContentModel[];
  getTopContentStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  [InsightsFilter.ALL_TIME]: {
    systemInsight: SystemInsightsModel;
    userInsight: UserInsightsModel;

    getSystemInsightStatus: {
      status: ReduxStatus;
      error: string | null;
    };
    getUserInsightStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [InsightsFilter.WEEKLY]: {
    systemInsight: SystemInsightsModel;
    userInsight: UserInsightsModel;

    getSystemInsightStatus: {
      status: ReduxStatus;
      error: string | null;
    };
    getUserInsightStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.CONTENT]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.NOTIFICATION]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.USER]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.GUEST]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.UPGRADE]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.GUEST_CONTENT]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
  [ExtendedAnalyticTypes.GUEST_NOTIFICATION]: {
    count: AnalyticCountModel;
    getCountStatus: {
      status: ReduxStatus;
      error: string | null;
    };
  };
}

const initialState: AnalyticsState = {
  logCount: [],
  getLogDateCountStatus: {
    status: 'idle',
    error: null,
  },
  topContent: [],
  getTopContentStatus: {
    status: 'idle',
    error: null,
  },
  [InsightsFilter.ALL_TIME]: {
    userInsight: {
      total: 0,
      registered: 0,
      unregistered: 0,
    },
    systemInsight: {
      conversions: 0,
      views: 0,
      notifications: 0,
      posts: 0,
    },
    getSystemInsightStatus: {
      status: 'idle',
      error: null,
    },
    getUserInsightStatus: {
      status: 'idle',
      error: null,
    },
  },
  [InsightsFilter.WEEKLY]: {
    userInsight: {
      total: 0,
      registered: 0,
      unregistered: 0,
    },
    systemInsight: {
      conversions: 0,
      views: 0,
      notifications: 0,
      posts: 0,
    },
    getSystemInsightStatus: {
      status: 'idle',
      error: null,
    },
    getUserInsightStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.CONTENT]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.NOTIFICATION]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.USER]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.GUEST]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.UPGRADE]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.GUEST_CONTENT]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
  [ExtendedAnalyticTypes.GUEST_NOTIFICATION]: {
    count: {
      totalCount: 0,
      weeklyCount: 0,
    },

    getCountStatus: {
      status: 'idle',
      error: null,
    },
  },
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAnalyticCount.pending, (state, { meta }) => {
      const { arg } = meta;
      state[arg].getCountStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getAnalyticCount.fulfilled, (state, { payload, meta }) => {
      const { arg } = meta;
      state[arg].getCountStatus = {
        status: 'success',
        error: null,
      };
      state[arg].count = payload;
    });
    builder.addCase(getAnalyticCount.rejected, (state, { payload, meta }) => {
      const { arg } = meta;
      state[arg].getCountStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : `Error trying to pull ${arg} counts`,
      };
    });
    builder.addCase(getSystemInsights.pending, (state, { meta }) => {
      const { arg } = meta;
      state[arg.filter].getSystemInsightStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getSystemInsights.fulfilled, (state, { payload, meta }) => {
      const { arg } = meta;
      state[arg.filter].systemInsight = payload;
      state[arg.filter].getSystemInsightStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(getSystemInsights.rejected, (state, { payload, meta }) => {
      const { arg } = meta;
      state[arg.filter].getSystemInsightStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : `Error trying to pull system insights with ${arg.filter}`,
      };
    });
    builder.addCase(getUserInsights.pending, (state, { meta }) => {
      const { arg } = meta;
      state[arg.filter].getUserInsightStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getUserInsights.fulfilled, (state, { payload, meta }) => {
      const { arg } = meta;
      state[arg.filter].userInsight = payload;
      state[arg.filter].getUserInsightStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(getUserInsights.rejected, (state, { payload, meta }) => {
      const { arg } = meta;
      state[arg.filter].getUserInsightStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : `Error trying to pull user insights with ${arg.filter}`,
      };
    });
    builder.addCase(getLogDateCount.pending, (state) => {
      state.getLogDateCountStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getLogDateCount.fulfilled, (state, { payload }) => {
      state.getLogDateCountStatus = {
        status: 'success',
        error: null,
      };
      state.logCount = payload;
    });
    builder.addCase(getLogDateCount.rejected, (state, { payload }) => {
      state.getLogDateCountStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : `Error trying to fetch log date counts`,
      };
    });
    builder.addCase(getTopContent.pending, (state) => {
      state.getTopContentStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getTopContent.fulfilled, (state, { payload }) => {
      state.getTopContentStatus = {
        status: 'success',
        error: null,
      };
      state.topContent = payload;
    });
    builder.addCase(getTopContent.rejected, (state, { payload }) => {
      state.getTopContentStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch top content',
      };
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export default analyticsSlice.reducer;
